import React, { useState, useEffect } from 'react';

import moment from 'moment';

import { toFullPrecisionString, truncateDecimals } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import FormInput from 'CommonComponents/FormInput';

function AutoAllocationModal(props) {
    const [autoMonths, setAutoMonths] = useState('');
    const [autoStartMonth, setAutoStartMonth] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Real-time validation effect
    useEffect(() => {
        if (autoMonths || autoStartMonth) {
            // Validate both fields when either changes
            const errors = {};

            // Validate autoMonths
            if (autoMonths) {
                const num = parseInt(autoMonths, 10);
                if (!autoMonths || autoMonths === '') {
                    errors.autoMonths = $t('This field is required');
                } else if (!num || num < 1) {
                    errors.autoMonths = $t('Number must be at least 1');
                } else if (autoStartMonth && props.maxDate) {
                    const start = moment(autoStartMonth, 'YYYY-MM');
                    const endDate = start
                        .clone()
                        .add(num - 1, 'months')
                        .endOf('month');
                    const maxDate = moment(props.maxDate);

                    if (endDate.isAfter(maxDate)) {
                        errors.autoMonths = $t('The selected number of months would exceed the maximum allowed date');
                    }
                }
            }

            // Validate autoStartMonth
            if (autoStartMonth) {
                if (!autoStartMonth || autoStartMonth === '') {
                    errors.autoStartMonth = $t('This field is required');
                } else if (autoMonths && props.maxDate) {
                    const num = parseInt(autoMonths, 10);
                    if (num > 0) {
                        const start = moment(autoStartMonth, 'YYYY-MM');
                        const endDate = start
                            .clone()
                            .add(num - 1, 'months')
                            .endOf('month');
                        const maxDate = moment(props.maxDate);

                        if (endDate.isAfter(maxDate)) {
                            errors.autoStartMonth = $t('The selected start month would result in dates exceeding the maximum allowed date');
                        }
                    }
                }
            }

            setFormErrors(errors);
        }
    }, [autoMonths, autoStartMonth, props.maxDate]);

    const validateAllFields = () => {
        const errors = {};
        let isValid = true;

        // Validate autoMonths
        const num = parseInt(autoMonths, 10);
        if (!autoMonths || autoMonths === '') {
            errors.autoMonths = $t('This field is required');
            isValid = false;
        } else if (!num || num < 1) {
            errors.autoMonths = $t('Number must be at least 1');
            isValid = false;
        }

        // Validate autoStartMonth
        if (!autoStartMonth || autoStartMonth === '') {
            errors.autoStartMonth = $t('This field is required');
            isValid = false;
        }

        // Check if the date range exceeds maxDate
        if (autoStartMonth && autoMonths && num > 0 && props.maxDate) {
            const start = moment(autoStartMonth, 'YYYY-MM');
            const endDate = start
                .clone()
                .add(num - 1, 'months')
                .endOf('month');
            const maxDate = moment(props.maxDate);

            if (endDate.isAfter(maxDate)) {
                errors.autoMonths = $t('The selected number of months would exceed the maximum allowed date');
                isValid = false;
            }
        }

        setFormErrors(errors);
        return isValid;
    };

    const onModalClose = () => {
        setAutoMonths('');
        setAutoStartMonth('');
        setFormErrors({});
        props.setShowAutoModal(false);
        props.setAllocationMode('manual');
    };

    const onSubmitAutoAllocation = () => {
        // Validate all fields before proceeding
        if (!validateAllFields()) {
            return;
        }

        const n = parseInt(autoMonths, 10);
        if (!n || n <= 0) return;
        if (!autoStartMonth) return;

        setIsSubmitting(true);

        // Use setTimeout to simulate processing time and show loader
        setTimeout(() => {
            const result = distributeEvenly(props.totalValue, n);

            // Even split with remainders corrected on last item
            const start = moment(autoStartMonth, 'YYYY-MM');
            const generated = result.percentages.map((percentageOriginal, index) => {
                const valueOriginal = result.parts[index];
                const percOriginalString = toFullPrecisionString(percentageOriginal);
                const valueOriginalString = toFullPrecisionString(valueOriginal);
                return {
                    id: null,
                    name: '',
                    date: start.clone().add(index, 'months').endOf('month').format('YYYY-MM-DD'),
                    description: null,
                    perc: truncateDecimals(percentageOriginal, 2),
                    percOriginal: percOriginalString,
                    value: truncateDecimals(valueOriginal, 2),
                    valueOriginal: valueOriginalString,
                    type: `fixed/${props.section}`,
                    canDeleteMilestones: true,
                    uniqueKey: Date.now() + index,
                };
            });
            props.setFieldValue('dates', generated);
            props.setShowAutoModal(false);
            props.setAllocationMode('manual');
            setIsSubmitting(false);
        }, 500);
    };

    const distributeEvenly = (total, partsCount) => {
        if (!partsCount || partsCount <= 0) {
            return { parts: [], percentages: [] };
        }

        const exactPart = total / partsCount;
        const exactPercentage = 100 / partsCount;

        const parts = Array.from({ length: partsCount }, () => exactPart);
        const percentages = Array.from({ length: partsCount }, () => exactPercentage);

        return { parts, percentages };
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/30 tw-z-50 tw-flex tw-items-center tw-justify-center">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-w-full tw-max-w-md tw-p-5">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                    <h4 className="tw-text-base tw-font-semibold">{$t('Allocate Automatically')}</h4>
                    <button className="tw-text-gray-500" onClick={onModalClose}>
                        ✕
                    </button>
                </div>
                <div className="tw-space-y-4">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <FormInput
                            horizontalLayout={false}
                            label={$t('Number of Months')}
                            required={true}
                            type="number"
                            id="autoMonths"
                            placeholder={$t('Enter number of months')}
                            value={autoMonths}
                            onChange={(e) => {
                                setAutoMonths(e.target.value);
                            }}
                            showError={formErrors.autoMonths}
                            errorText={formErrors.autoMonths}
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-sm tw-text-gray-700 tw-mb-1">{$t('Start Month')} *</label>
                        <DatePickerField
                            name="autoStartMonth"
                            value={autoStartMonth}
                            onChange={(name, value) => {
                                setAutoStartMonth(value);
                            }}
                            dateFormat="yyyy-MM"
                            showMonthYearPicker={true}
                            min={props.minDate}
                            max={props.maxDate}
                            className="tw-w-full text-input !tw-bg-grey/50 tw-border-0 tw-rounded tw-px-2 tw-py-1"
                        />
                        {formErrors.autoStartMonth && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{formErrors.autoStartMonth}</div>}
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-6">
                    <Button variant="secondary" onClick={onModalClose}>
                        {$t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={onSubmitAutoAllocation} disabled={isSubmitting}>
                        {isSubmitting ? $t('Processing...') : $t('Submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default AutoAllocationModal;
